import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { FaChevronRight, FaMapMarkerAlt, FaCircle } from "react-icons/fa";
import { GiSteeringWheel } from "react-icons/gi";
import { baseurl } from "../../baseurl";
import "../../asset/css/bus-results-new.css";

const BusSeats = () => {
  const { id, source, desti } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [seats, setSeats] = useState([]);
  const [boardingPoints, setBoardingPoints] = useState([]);
  const [droppingPoints, setDroppingPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Markup State ---
  const [busMarkup, setBusMarkup] = useState({ type: "flat", amount: 0 });

  const [selectedSeats, setSelectedSeats] = useState([]);
  const [passengers, setPassengers] = useState({});
  const [contactInfo, setContactInfo] = useState({ email: "", phone: "" });
  const [selectedBoardingId, setSelectedBoardingId] = useState("");
  const [selectedDroppingId, setSelectedDroppingId] = useState("");

  // --- MARKUP LOGIC FUNCTION ---
  // This calculates the extra amount to be added
  const getMarkupValue = useCallback((baseFare) => {
    const fare = parseFloat(baseFare) || 0;
    const markupAmt = parseFloat(busMarkup.amount) || 0;
    const type = busMarkup.type?.toLowerCase();
    
    let addedValue = 0;

    if (type === "percent" || type === "percentage") {
      addedValue = fare * (markupAmt / 100);
    } else {
      addedValue = markupAmt;
    }

    return addedValue;
  }, [busMarkup]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // 1. Fetch Markup Data
        const markupRes = await axios.post(`${baseurl}/getServiceMarkupsGuest`);
        const busService = markupRes.data.markups?.find(m => Number(m.service_id) === 6);
        if (busService) {
          setBusMarkup({
            type: busService.markup_type,
            amount: busService.amount
          });
          console.log(`%c Markup Config: ${busService.amount} (${busService.markup_type})`, "color: blue; font-weight: bold;");
        }

        // 2. Fetch Seat Layout
        const payload = { inventoryId: id };
        const response = await axios.post(`${baseurl}/getTripDetailsV2`, payload);
        const tripData = response.data.data || {};
        setSeats(tripData.seats || []);
        const bData = tripData.boardingTimes;
        setBoardingPoints(Array.isArray(bData) ? bData : (bData ? [bData] : []));
        const dData = tripData.droppingTimes;
        setDroppingPoints(Array.isArray(dData) ? dData : (dData ? [dData] : []));
      } catch (err) {
        console.error("Error fetching trip details", err);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchData();
  }, [id]);

  const handleSeatClick = (seat) => {
    if (seat.available === "false") return;
    const isSelected = selectedSeats.find((s) => s.name === seat.name);
    if (isSelected) {
      setSelectedSeats(selectedSeats.filter((s) => s.name !== seat.name));
      const newPass = { ...passengers };
      delete newPass[seat.name];
      setPassengers(newPass);
    } else {
      if (selectedSeats.length >= 6) return alert("Max 6 seats allowed");
      setSelectedSeats([...selectedSeats, seat]);
      setPassengers({ 
        ...passengers, 
        [seat.name]: { title: "Mr", name: "", age: "", gender: "Male" } 
      });
    }
  };

  const handlePassengerChange = (seatName, field, value) => {
    setPassengers(prev => ({ ...prev, [seatName]: { ...prev[seatName], [field]: value } }));
  };

const handleSubmit = async () => {
    if (!selectedBoardingId || !selectedDroppingId) return alert("Please select both boarding and dropping points.");
    if (selectedSeats.length === 0) return alert("Please select at least one seat.");
    
    const bPoint = boardingPoints.find(p => String(p.bpId) === String(selectedBoardingId));
    const dPoint = droppingPoints.find(p => String(p.bpId) === String(selectedDroppingId));
    if (!bPoint || !dPoint) return alert("Could not resolve Boarding or Dropping point data.");

    const blockPayload = {
      availableTripID: id, 
      boardingPointId: bPoint.bpId,
      droppingPointId: dPoint.bpId,
      source: source,
      destination: desti,
      // We send raw fare to satisfy the provider's validation
      inventoryItems: selectedSeats.map((seat, i) => ({
        seatName: seat.name,
        fare: parseFloat(seat.fare), 
        ladiesSeat: seat.ladiesSeat === "true",
        passenger: [{
          title: passengers[seat.name]?.title || "Mr",
          name: (passengers[seat.name]?.name || "").trim(),
          age: parseInt(passengers[seat.name]?.age, 10) || 0,
          gender: passengers[seat.name]?.gender,
          primary: i === 0 ? "true" : "false",
          mobile: contactInfo.phone,
        }]
      })),
      // Sending markup info to your backend for calculation
      appliedMarkupType: busMarkup.type,
      appliedMarkupAmount: busMarkup.amount
    };

    console.log("Block Payload:", blockPayload);

    try {
      setLoading(true);
      const response = await axios.post(`${baseurl}/blockTicket`, blockPayload);

      if (response.data) {
        // --- Navigation Logic ---
        navigate('/confirm-booking', { 
          state: { 
            bookingDetails: response.data,
            email: contactInfo.email,
            phone: contactInfo.phone,
            totalFare: totalFare, // (Base + Markup)
            boardingName: bPoint.bpName,
            droppingName: dPoint.bpName,
            // Correctly passing state values to the next page
            appliedMarkupType: busMarkup.type,
            appliedMarkupAmount: busMarkup.amount
          } 
        });
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.response?.data?.message || "Failed to block seats.";
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  
  // UI Calculation: Base + Markup
  const totalFare = Math.round(selectedSeats.reduce((acc, s) => {
    const base = parseFloat(s.fare);
    return acc + base + getMarkupValue(base);
  }, 0));

  const renderDeck = (deckSeats, title) => {
    if (deckSeats.length === 0) return null;
    const maxRow = Math.max(...deckSeats.map(s => parseInt(s.row))) + 1;
    const maxCol = Math.max(...deckSeats.map(s => parseInt(s.column))) + 1;

    return (
      <div className="deck-section">
        <span className="deck-label">{title}</span>
        <div className="bus-horizontal-container">
          <div className="bus-nose">
            <GiSteeringWheel className="driver-wheel" />
          </div>
          <div 
            className="seat-grid-canvas"
            style={{
              gridTemplateRows: `repeat(${maxRow}, 35px)`,
              gridTemplateColumns: `repeat(${maxCol}, auto)`
            }}
          >
            {deckSeats.map((seat, index) => {
              const isSelected = selectedSeats.find((s) => s.name === seat.name);
              const isSleeper = parseInt(seat.length) > 1;
              const isBooked = seat.available === "false";
              const isLady = seat.ladiesSeat === "true";

              return (
                <div
                  key={index}
                  className={`seat-unit ${isBooked ? "booked" : "available"} 
                             ${isLady ? "lady-only" : ""} 
                             ${isSelected ? "selected" : ""} 
                             ${isSleeper ? "sleeper-horizontal" : "seater-unit"}`}
                  style={{
                    gridRow: `${parseInt(seat.row) + 1} / span ${seat.width}`,
                    gridColumn: `${parseInt(seat.column) + 1} / span ${seat.length}`,
                  }}
                  onClick={() => handleSeatClick(seat)}
                >
                  <div className="seat-inner">
                    <span className="seat-id">{seat.name}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

if (loading) {
  return (
    <div className="loader-overlay">
      <div className="loader-content">
        <div className="spinner-border text-danger" role="status"></div>
        <p className="loader-text">Loading Bookings...</p>
      </div>
    </div>
  );
}



  return (
    <div className="modern-bus-page" style={{marginTop: "100px"}}>
      <div className="container py-4">
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="modern-card mb-4 p-4">
              <h6 className="fw-bold mb-3"><FaMapMarkerAlt className="text-danger me-2"/>Travel Points</h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small text-muted">Boarding Point</label>
                  <select className="form-select modern-select" value={selectedBoardingId} onChange={(e) => setSelectedBoardingId(e.target.value)}>
                    <option value="">Select Boarding</option>
                    {boardingPoints.map((bp) => <option key={bp.bpId} value={bp.bpId}>{bp.bpName}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small text-muted">Dropping Point</label>
                  <select className="form-select modern-select" value={selectedDroppingId} onChange={(e) => setSelectedDroppingId(e.target.value)}>
                    <option value="">Select Dropping</option>
                    {droppingPoints.map((dp) => <option key={dp.bpId} value={dp.bpId}>{dp.bpName}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="modern-card p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h6 className="fw-bold m-0">Select Seats</h6>
                <div className="seat-legend-mini">
                  <div className="leg"><span className="sq avail"></span> Avail</div>
                  <div className="leg"><span className="sq book"></span> Booked</div>
                  <div className="leg"><span className="sq lady"></span> Lady</div>
                  <div className="leg"><span className="sq sel"></span> Chosen</div>
                </div>
              </div>
              <div className="layout-scroll-wrapper">
                {renderDeck(seats.filter(s => String(s.zIndex) === "0"), "LOWER DECK")}
                {renderDeck(seats.filter(s => String(s.zIndex) === "1"), "UPPER DECK")}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="modern-card">
              <div className="sidebar-header">Booking Summary</div>
              <div className="p-4">
                {selectedSeats.length > 0 ? (
                  <>
                    <div className="mb-4">
                       <input type="email" className="form-control mb-2" placeholder="Email Address" value={contactInfo.email} onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})} />
                       <input type="text" className="form-control" placeholder="Mobile Number" value={contactInfo.phone} onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})} />
                    </div>
                    <div className="passenger-list-box">
                      {selectedSeats.map((seat) => {
                        const base = parseFloat(seat.fare);
                        const markup = getMarkupValue(base);
                        const displayFare = Math.round(base + markup);
                        
                        console.log(`UI Log -> Seat ${seat.name}: Base ${base} + Markup ${markup.toFixed(2)} = ${displayFare}`);
                        
                        return (
                          <div key={seat.name} className="pass-row mb-3 p-2 border rounded">
                            <div className="d-flex justify-content-between mb-2">
                              <span className="badge bg-light text-dark">Seat {seat.name}</span>
                              <span className="fw-bold">₹{displayFare}</span>
                            </div>
                            <input type="text" className="form-control form-control-sm mb-2" placeholder="Name" value={passengers[seat.name]?.name} onChange={(e) => handlePassengerChange(seat.name, "name", e.target.value)} />
                            <div className="row g-2">
                              <div className="col-6"><input type="number" className="form-control form-control-sm" placeholder="Age" value={passengers[seat.name]?.age} onChange={(e) => handlePassengerChange(seat.name, "age", e.target.value)} /></div>
                              <div className="col-6">
                                <select className="form-select form-select-sm" value={passengers[seat.name]?.gender} onChange={(e) => handlePassengerChange(seat.name, "gender", e.target.value)}>
                                  <option value="Male">Male</option>
                                  <option value="Female">Female</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 pt-3 border-top">
                      <div className="d-flex justify-content-between mb-3">
                        <span className="text-muted">Total fare</span>
                        <h4 className="fw-bold m-0 text-danger">₹{totalFare}</h4>
                      </div>
                      <button className="btn btn-danger w-100 py-2 fw-bold" onClick={handleSubmit}>
                        PROCEED TO PAY <FaChevronRight size={12}/>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Select seats to continue</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusSeats;